<template>
  <wui-modal
    :title="modalTitle"
    :size="40"
    :closable="true"
  >
    <div class="text-center px-4 py-6">
      <wui-icon
        :name="schedule ? 'taskAlt' : 'infoOutline'"
        class="w-16! h-16! text-primary!"
      />
      <div class="pt-6 pb-6">
        <template v-if="schedule">
          <div class="text-1324 text-fg-base-fg10">
            {{ scheduleSummary }}
          </div>
        </template>
      </div>
      <div class="pb-2 text-1624 font-light text-fg-base-fg10">
        {{ $t('common.participants') }}
      </div>
      <bubble-contact-list
        v-if="room"
        :contacts="resolvedContacts"
        :external-contacts-numbers="externalContactsNumbers"
        class="px-4 pb-8 justify-center"
      />
      <schedule-download-ics
        v-if="schedule"
        :schedule="schedule"
        info
        class="mx-auto"
      />
    </div>

    <template #footer>
      <wui-btn
        :size="40"
        data-test="schedule-edit-success-close-modal-btn"
        variant="neut"
        state="alpha"
        @click="close()"
      >
        {{ $t('common.close') }}
      </wui-btn>
    </template>
  </wui-modal>
</template>
<script lang="ts" setup>
import { WuiIcon, WuiBtn, WuiModal, closeDialogKey, safeInject } from '@wui/common-library'
import { PServed } from '@wui/im'
import { computed } from 'vue'


import { ScheduleDownloadIcs, formatScheduleNotificationText } from '@/features/schedule'

import { BubbleContactList, useStoreContacts } from '@/entities/contact'
import { useStoreRooms } from '@/entities/room'
import { useRoomScheduleStore } from '@/entities/schedule'

import { useCurrentUser } from '@/shared/composables/current-user'
import { useLocalization } from '@/shared/composables/localization'

const props = defineProps<{
  pServed: PServed,
  participantsNumbers?: string[],
  uid: string,
  isCreating?: boolean
}>()

const { t } = useLocalization()
const { findByPServed: findRoom } = useStoreRooms()
const { findByPServed: findSchedule } = useRoomScheduleStore()
const { findByInternalNumber, findContactByMobilePhone } = useStoreContacts()
const { currentUser } = useCurrentUser()

const schedule = computed(() => {
  return findSchedule(props.pServed)?.find(e => e.uid === props.uid)
})

const scheduleSummary = computed(() => {
  if (!schedule.value) {
    return ''
  }

  return formatScheduleNotificationText(schedule.value)
})

const modalTitle = computed(() => {
  if (!schedule.value) {
    return t('calendar.scheduleRemoved')
  }

  return props.isCreating ? t('calendar.scheduleUpdated') : t('calendar.scheduleEdited')
})

const room = computed(() => {
  return findRoom(props.pServed)
})

const resolvedContacts = computed(() =>
  props.participantsNumbers
    ?.map(number => {
      if (number === currentUser.value?.internalNumber) {
        return currentUser.value
      }

      return findByInternalNumber(number) ?? findContactByMobilePhone(number)
    })
    .filter((contact): contact is NonNullable<typeof contact> => contact !== undefined) ?? [])

const externalContactsNumbers = computed(() => props.participantsNumbers?.filter(number =>
  number !== currentUser.value?.internalNumber
  && !findByInternalNumber(number)
  && !findContactByMobilePhone(number),
) ?? [])

const closeDialog = safeInject(closeDialogKey)

const close = () => {
  closeDialog(undefined)
}
</script>
